import {Box, Icon, ToggleButton, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {
    AGUARDANDO_APROVACAO,
    EM_FILA,
    PROCESSADO_COM_ERRO,
    PROCESSADO_COM_SUCESSO,
    PROCESSAMENTO,
    RESULTADO_INDETERMINADO
} from "../../shared/estados";
import {useListQueryParam, useQuery} from "../../shared/useQueryParam";

const FILTROS = [
    {estado: null, label: "Todas", icone: "layers", color: "primary"},
    {
        estado: AGUARDANDO_APROVACAO,
        label: "Aprovação",
        icone: "fact_check",
        color: "info"
    },
    {estado: EM_FILA, label: "Em fila", icone: "schedule", color: "primary"},
    {
        estado: PROCESSAMENTO,
        label: "Em andamento",
        icone: "sync",
        color: "warning"
    },
    {
        estado: PROCESSADO_COM_SUCESSO,
        label: "Etapa concluída",
        icone: "check_circle",
        color: "success"
    },
    {
        estado: PROCESSADO_COM_ERRO,
        label: "Com erro",
        icone: "error_outline",
        color: "error"
    },
    {
        estado: RESULTADO_INDETERMINADO,
        label: "Indeterminado",
        icone: "help_outline",
        color: "warning"
    }
];

function FiltroEstados({contagemEstado = []}) {
    const estados = useListQueryParam("estados");
    const query = useQuery();
    const navigate = useNavigate();

    function handleEstado(estado) {
        const novosEstados =
            estado === null
                ? []
                : estados.includes(estado)
                ? estados.filter((atual) => atual !== estado)
                : [...estados, estado];
        const params = new URLSearchParams(query);
        if (novosEstados.length) {
            params.set("estados", novosEstados.join(","));
        } else {
            params.delete("estados");
        }
        params.set("page", "0");
        navigate(`/?${params}`);
    }

    return (
        <Box
            role="group"
            aria-label="Filtrar ocorrências por estado"
            sx={{
                display: "grid",
                gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(3, minmax(0, 1fr))",
                    lg: "repeat(7, minmax(0, 1fr))"
                },
                gap: 1,
                width: "100%",
                minWidth: 0,
                my: 2
            }}
        >
            {FILTROS.map(({estado, color, label, icone}) => {
                const selecionado =
                    estado === null
                        ? !estados.length
                        : estados.includes(estado);
                const total = contagemEstado
                    .filter((item) => estado === null || item.estado === estado)
                    .reduce((soma, item) => soma + item.total, 0)
                    .toLocaleString("pt-BR");

                return (
                    <ToggleButton
                        key={estado ?? "todas"}
                        value={estado ?? "todas"}
                        selected={selecionado}
                        onChange={() => handleEstado(estado)}
                        aria-label={`${label}: ${total} ocorrências`}
                        sx={{
                            gridColumn:
                                estado === null
                                    ? {xs: "1 / -1", lg: "auto"}
                                    : undefined,
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                            minWidth: 0,
                            minHeight: 80,
                            px: 1,
                            py: 1.25,
                            borderRadius: 1,
                            borderColor: "divider",
                            bgcolor: "background.paper",
                            color: "text.secondary",
                            textTransform: "none",
                            transition: (theme) =>
                                theme.transitions.create([
                                    "background-color",
                                    "border-color"
                                ]),
                            "&:hover": {
                                bgcolor: "action.hover",
                                borderColor: `${color}.main`
                            },
                            "&.Mui-selected": {
                                bgcolor: `${color}.main`,
                                color: `${color}.contrastText`,
                                borderColor: `${color}.main`,
                                "&:hover": {bgcolor: `${color}.dark`}
                            },
                            "&.Mui-focusVisible": {
                                outline: "3px solid",
                                outlineColor: `${color}.main`,
                                outlineOffset: 2
                            }
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.75
                            }}
                        >
                            <Icon
                                fontSize="small"
                                color={selecionado ? "inherit" : color}
                            >
                                {icone}
                            </Icon>
                            <Typography
                                component="span"
                                variant="body2"
                                fontWeight={selecionado ? 600 : 400}
                            >
                                {label}
                            </Typography>
                        </Box>
                        <Typography
                            component="span"
                            variant="h5"
                            fontWeight={700}
                            sx={{
                                fontVariantNumeric: "tabular-nums",
                                lineHeight: 1.2,
                                overflowWrap: "anywhere"
                            }}
                        >
                            {total}
                        </Typography>
                    </ToggleButton>
                );
            })}
        </Box>
    );
}

export default FiltroEstados;
